import { useState, useEffect, useMemo } from 'react';
import { useTalentListDataLoader } from './useTalentListDataLoader';

type UseTalentListNamesResult = {
  names: Map<string, string>;
  loading: boolean;
};

export const useTalentListNames = (
  talentListIds: string[]
): UseTalentListNamesResult => {
  const loader = useTalentListDataLoader();
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);

  // Memoize the IDs to prevent unnecessary re-fetches
  const idsKey = useMemo(() => talentListIds.join(','), [talentListIds]);

  useEffect(() => {
    let mounted = true;

    if (talentListIds.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);

    loader
      .loadMany(talentListIds)
      .then((results) => {
        if (!mounted) return;

        const nameMap = new Map<string, string>();
        results.forEach((result, index) => {
          if (result && !(result instanceof Error)) {
            nameMap.set(talentListIds[index], result.title);
          }
        });
        setNames(nameMap);
        setLoading(false);
      })
      .catch(() => {
        if (!mounted) return;

        setNames(new Map());
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [idsKey, loader]);

  return { names, loading };
};
